<template>
  <template v-if="$slots.legend">
    <slot name="legend" :toggles="legendToggles" :data-sets="dataSets" />
  </template>
  <component
    :is="isFinance ? 'LineChartWithOrigin' : 'LineChart'"
    :data="chartData"
    :options="chartOptions"
  />
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { useThemeHelper } from "@/theme/themeHelper"
  import { Line as LineChart } from "vue-chartjs"
  import "chartjs-adapter-moment"
  import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ChartData,
    ChartOptions,
    TimeScale,
    ChartDataset,
    Color,
    ScriptableContext,
  } from "chart.js"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import LineChartWithOrigin from "./LineChartWithOrigin.js"
  import dayjs, { Dayjs } from "dayjs"

  ChartJS.register(
    Title,
    Tooltip,
    Legend,
    Filler,
    LineElement,
    CategoryScale,
    LinearScale,
    PointElement,
    TimeScale,
  )

  type ChartJsColor =
    | string
    | ((
        ctx: ScriptableContext<"line">,
        options: Record<string, object>,
      ) => Color)

  export interface DataSet {
    label: string
    data: Array<number | null>
    lineColor: ChartJsColor
    lineType?: "dashed" | "crossed" | "default"
    pointStyle?: string
    hideLine?: boolean
    hidePoints?: boolean
    backgroundColor?: ChartJsColor
    fill?: boolean
  }

  const DEFAULT_POINT_RADIUS = 4
  const DEFAULT_BORDER_WIDTH = 2
  const DEFAULT_POINT_WIDTH = 2

  export default defineComponent({
    components: { LineChart, LineChartWithOrigin },
    props: {
      title: {
        type: String,
        default: null,
      },
      labels: {
        type: Array as PropType<Array<string | Dayjs>>,
        required: true,
      },
      dataSets: {
        type: Array as PropType<Array<DataSet>>,
        required: true,
        default: () => [],
      },
      /** Width / Height. If not set, the chart will be responsive. */
      aspectRatio: {
        type: Number,
        default: undefined,
      },
      isFinance: {
        type: Boolean,
        default: false,
      },
      /** Disable clicking legend labels to toggle dataset visibility. */
      disableLegendToggle: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        currentTheme: useThemeHelper(),
        legendToggles: [] as boolean[],
      }
    },
    computed: {
      chartData() {
        const chartDatasets = this.dataSets.map((dataset, index) => {
          const chartDataset: ChartDataset<"line"> = {
            label: dataset.label,
            data: dataset.data,
            backgroundColor: dataset.backgroundColor,
            borderColor: dataset.lineColor,
            showLine: !dataset.hideLine,
            pointStyle: dataset.pointStyle ?? false,
            pointRadius: dataset.hidePoints ? 0 : DEFAULT_POINT_RADIUS,
            borderWidth: DEFAULT_BORDER_WIDTH,
            pointBorderWidth: DEFAULT_POINT_WIDTH,
            fill: dataset.fill,
            hidden: this.$slots.legend && !this.legendToggles[index],
          }
          if (dataset.lineType === "dashed") {
            chartDataset.borderDash = [10, 5]
          }
          if (dataset.lineType === "crossed") {
            chartDataset.pointStyle = "cross"
            chartDataset.pointBorderWidth = 20
            chartDataset.pointRotation = 45
          }

          return chartDataset
        })

        const chartData: ChartData<"line"> = {
          labels: this.chartLabels,
          datasets: chartDatasets,
        }
        return chartData
      },
      chartOptions() {
        const chartOptions: ChartOptions<"line"> = {
          aspectRatio: this.aspectRatio,
          interaction: {
            intersect: false,
            mode: "index",
          },
          maintainAspectRatio: !!this.aspectRatio,
          color: this.currentTheme.colors["on-background"],
          plugins: {
            legend: {
              display: !this.$slots.legend,
              // A no-op onClick keeps the cursor from implying the labels are
              // interactive and stops the default "toggle dataset" behaviour.
              ...(this.disableLegendToggle && {
                onClick: () => {
                  /* no-op: legend toggling is disabled */
                },
              }),
              labels: {
                pointStyle: "line",
                usePointStyle: true,
              },
            },
            tooltip: {
              usePointStyle: true,
              position: "nearest",
              callbacks: {
                label: (context) => {
                  if (!this.isFinance) return
                  let label = context.dataset.label ?? ""

                  if (label) label += ": "
                  if (context.parsed.y !== null) {
                    label += formatMoney({
                      amount: Number(context.parsed.y),
                      round: true,
                    })
                  }
                  return label
                },
                labelPointStyle: () => {
                  return {
                    pointStyle: "line",
                    rotation: 0,
                  }
                },
              },
            },
          },
          scales: {
            y: {
              stacked: false,
              ticks: {
                color: this.currentTheme.colors["on-background"],
                callback: (value) =>
                  this.isFinance
                    ? formatMoney({ amount: Number(value), round: true })
                    : value,
              },
              grid: {
                color: this.currentTheme.colors["border"],
              },
            },
            x: {
              ...(this.isTimeChart && { type: "time" }),
              stacked: false,
              ticks: {
                color: this.currentTheme.colors["on-background"],
              },
              grid: {
                color: this.currentTheme.colors["border"],
              },
            },
          },
        }
        return chartOptions
      },
      isTimeChart() {
        return this.labels.some((label) => dayjs.isDayjs(label))
      },
      chartLabels() {
        return this.labels.map((label: string | Dayjs) => {
          if (this.isTimeChart) {
            return (label as Dayjs).toISOString()
          } else {
            return label
          }
        })
      },
    },
    mounted() {
      this.dataSets.forEach(() => {
        this.legendToggles.push(true)
      })
    },
  })
</script>
